import { useState, useEffect } from 'react'
import { useCrosswordStore } from '../store'
import { findAllCrossingsForPair } from '../lib/search'
import type { CrossingWithPositions } from '../lib/search'
import { getLetters } from '../lib/pairings'
import { StatusBadge } from './StatusFilter'
import { CrosswordPair } from './CrosswordText'
import { DiptychGrid } from './CrosswordViewColumn'
import type { Pairing, PairingStatus } from '../types'

type Orientation = 'horizontal' | 'vertical'

type Props = {
  pairing: Pairing
  onClose: () => void
}

const STATUS_OPTIONS: (PairingStatus | 'all')[] = ['all', 'none', 'draft', 'approved', 'rejected']

export function CrossingsPanel({ pairing, onClose }: Props) {
  const { words, pairings, getOrCreatePairing, setSelectedPairing } = useCrosswordStore()
  const [statusFilter, setStatusFilter] = useState<PairingStatus | 'all'>('all')
  const [orientation, setOrientation] = useState<Orientation>('horizontal')
  const [selectedPosition, setSelectedPosition] = useState<number | null>(null)
  const [hoveredPosition, setHoveredPosition] = useState<number | null>(null)

  // Reset the position filter when the selected pair changes.
  useEffect(() => {
    setSelectedPosition(null)
  }, [pairing.id])

  const wordA = words.find(w => w.id === pairing.wordAId)
  const wordB = words.find(w => w.id === pairing.wordBId)

  const allCrossings = (() => {
    if (!wordA || !wordB) return []
    return findAllCrossingsForPair(wordA, wordB, words, pairings)
  })()

  const filteredCrossings = (() => {
    let result = allCrossings

    if (selectedPosition !== null) {
      result = result.filter(c => c.positions.includes(selectedPosition))
    }

    if (statusFilter !== 'all') {
      result = result.filter(c =>
        c.pairing ? c.pairing.status === statusFilter : statusFilter === 'none'
      )
    }

    const statusOrder: Record<string, number> = {
      'approved': 1, 'draft': 2, 'none': 3, 'rejected': 4
    }

    result = [...result].sort((a, b) => {
      const statusA = a.pairing?.status || 'none'
      const statusB = b.pairing?.status || 'none'

      if (statusOrder[statusA] !== undefined && statusOrder[statusB] !== undefined && statusOrder[statusA] - statusOrder[statusB] !== 0) {
        return statusOrder[statusA] - statusOrder[statusB]
      }

      const lenA = getLetters(a.wordA).length
      const lenB = getLetters(b.wordA).length
      return lenB - lenA
    })

    return result
  })()

  const maxCrossLength = Math.max(
    10,
    ...allCrossings.map(c => getLetters(c.wordA).length)
  )

  if (!wordA || !wordB) return null

  const handleSelectCrossing = (crossing: CrossingWithPositions) => {
    const crossingPairing = crossing.pairing || getOrCreatePairing(crossing.wordA.id, crossing.wordB.id)
    if (crossingPairing) setSelectedPairing(crossingPairing.id)
  }

  const handleLetterClick = (idx: number) => {
    setSelectedPosition(prev => prev === idx ? null : idx)
  }

  return (
    <div className="flex flex-col h-full overflow-hidden bg-white rounded-xl shadow-sm">
      {/* Header */}
      <div className="px-4 py-2.5 bg-gray-50/50 flex items-center justify-between shrink-0 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">
            {filteredCrossings.length} crossings
          </span>
          <button
            onClick={() => setOrientation(o => o === 'horizontal' ? 'vertical' : 'horizontal')}
            className="w-6 h-6 flex items-center justify-center rounded border border-gray-200 bg-white hover:bg-gray-50 text-gray-400 hover:text-gray-600 transition-colors"
            title={`Switch to ${orientation === 'horizontal' ? 'vertical' : 'horizontal'}`}
          >
            {orientation === 'horizontal' ? '━' : '┃'}
          </button>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value as PairingStatus | 'all')}
            className="text-xs px-2.5 py-1.5 border border-gray-200 rounded-md bg-white"
          >
            {STATUS_OPTIONS.map(status => (
              <option key={status} value={status}>
                {status === 'all' ? 'All' : status.charAt(0).toUpperCase() + status.slice(1)}
              </option>
            ))}
          </select>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            title="Close crossings panel"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Position filter — click a letter to filter crossings to that index */}
      <div className="px-4 py-3 shrink-0 border-b border-gray-100 bg-white flex flex-col items-center gap-1.5">
        <CrosswordPair
          textA={getLetters(wordA)}
          textB={getLetters(wordB)}
          autoHighlight
          highlightIndex={selectedPosition ?? hoveredPosition}
          onLetterClick={handleLetterClick}
          onLetterHover={setHoveredPosition}
        />
        <p className="text-[11px] text-gray-400">
          {selectedPosition !== null ? (
            <button
              onClick={() => setSelectedPosition(null)}
              className="hover:text-gray-600 transition-colors"
            >
              Filtering position {selectedPosition + 1} · Clear
            </button>
          ) : (
            'Click a letter to filter by position'
          )}
        </p>
      </div>

      {/* Crossings list */}
      <div className="flex-1 overflow-y-auto bg-gray-50/30 p-3">
        <div className="flex flex-col gap-2">
          {filteredCrossings.map(crossing => {
            const status = crossing.pairing?.status
            const showStatus = status && status !== 'none'
            return (
              <button
                key={`${crossing.wordA.id}-${crossing.wordB.id}`}
                onClick={() => handleSelectCrossing(crossing)}
                className="p-3 rounded-xl bg-white hover:bg-gray-50 transition-all hover:shadow-sm flex items-center justify-center relative group"
              >
                {showStatus && (
                  <div className="absolute top-2 right-2">
                    <StatusBadge status={status} compact />
                  </div>
                )}
                <DiptychGrid
                  mainWordA={wordA}
                  mainWordB={wordB}
                  crossWordA={crossing.wordA}
                  crossWordB={crossing.wordB}
                  crossPosition={crossing.positions[0] ?? 0}
                  orientation={orientation}
                  maxCrossLength={maxCrossLength}
                />
              </button>
            )
          })}
        </div>

        {filteredCrossings.length === 0 && (
          <p className="text-center py-12 text-gray-400 text-sm">
            No crossings found
          </p>
        )}
      </div>
    </div>
  )
}

